const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (_) {
    return 'UTC';
  }
};

export const IANA_TIMEZONE_OPTIONS = (() => {
  let supported = [];
  try {
    supported = typeof Intl.supportedValuesOf === 'function'
      ? Intl.supportedValuesOf('timeZone')
      : [];
  } catch (_) {
    supported = [];
  }
  // UTC is a valid IANA identifier, but ECMA-402 intentionally omits it from
  // supportedValuesOf('timeZone') because that API returns named zones only.
  return [...new Set(['UTC', ...supported])].sort((left, right) => left.localeCompare(right));
})();

export const resolvedInstitutionTimeZone = (...candidates) => {
  const values = [...candidates, browserTimeZone(), 'UTC'];
  for (const candidate of values) {
    const timezone = String(candidate || '').trim();
    if (!timezone) continue;
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: timezone }).format();
      return timezone;
    } catch (_) {
      // Continue to the browser timezone and ultimately UTC.
    }
  }
  return 'UTC';
};

const formatterFor = (timezone) => new Intl.DateTimeFormat('en-CA', {
  timeZone: timezone,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

const zonedParts = (date, timezone) => Object.fromEntries(
  formatterFor(timezone)
    .formatToParts(date)
    .filter(({ type }) => type !== 'literal')
    .map(({ type, value }) => [type, Number(value)]),
);

const parsedLocalValue = (value) => {
  const match = String(value || '').trim().match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/,
  );
  if (!match) return null;
  const [, year, month, day, hour, minute, second = '0'] = match;
  const parts = {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
    second: Number(second),
  };
  const validationDate = new Date(Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  ));
  if (
    validationDate.getUTCFullYear() !== parts.year
    || validationDate.getUTCMonth() + 1 !== parts.month
    || validationDate.getUTCDate() !== parts.day
    || parts.hour > 23
    || parts.minute > 59
    || parts.second > 59
  ) return null;
  return parts;
};

const utcWallClockMilliseconds = (parts) => Date.UTC(
  parts.year,
  parts.month - 1,
  parts.day,
  parts.hour,
  parts.minute,
  parts.second || 0,
);

const timezoneOffsetMilliseconds = (date, timezone) => (
  utcWallClockMilliseconds(zonedParts(date, timezone)) - date.getTime()
);

/** Convert a datetime-local wall-clock value in an institution timezone to UTC. */
export const institutionLocalDateTimeToIso = (value, timezone) => {
  const target = parsedLocalValue(value);
  if (!target) return '';
  const zone = resolvedInstitutionTimeZone(timezone);
  const targetMilliseconds = utcWallClockMilliseconds(target);
  let candidate = targetMilliseconds - timezoneOffsetMilliseconds(
    new Date(targetMilliseconds),
    zone,
  );
  // Re-evaluate once at the candidate instant to handle zones whose offset at
  // the target wall clock differs from the first UTC estimate (DST changes).
  candidate = targetMilliseconds - timezoneOffsetMilliseconds(new Date(candidate), zone);
  const result = new Date(candidate);
  const actual = zonedParts(result, zone);
  const matches = ['year', 'month', 'day', 'hour', 'minute', 'second']
    .every((key) => actual[key] === target[key]);
  return matches ? result.toISOString() : '';
};

/** Render an instant as a datetime-local value in an institution timezone. */
export const institutionDateTimeInputValue = (value, timezone) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = zonedParts(date, resolvedInstitutionTimeZone(timezone));
  const pad = (number) => String(number).padStart(2, '0');
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`;
};

/** Display an instant with both its institution-local time and explicit zone. */
export const formatInstitutionDateTime = (value, timezone) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Invalid date';
  const zone = resolvedInstitutionTimeZone(timezone);
  const formatted = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date);
  return `${formatted} (${zone})`;
};
